import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { listDuelSpecKeys } from '../../domain/game/duelSpecs';
import { specLabelKey } from '../../domain/cars/carProperties';
import { baseDirection, isPlayableOn } from '../../domain/game/overtakeCards';
import { duelSubjectIcon } from './duelSubjects';

/**
 * The figures, as things to call (SPEC-011 D-16, D-22, D-29).
 *
 * A ROW OF PICTOGRAMS WITH THEIR NAMES UNDER THEM, five of them and a way to
 * the rest. It was a rail of eleven marks with no text, on the argument that a
 * board should never change size as figures are consumed — and that argument
 * cost the row the one thing it is for. A reader deciding what to call is
 * choosing between TOP SPEED and TORQUE, not between two grey lozenges, and
 * eleven labelled chips do not fit above a hand on a phone. So five are drawn
 * with their glyph and their word, MORE opens the other six, and the whole
 * record is one tap away instead of eleven marks wide.
 *
 * WHAT IS SPENT IS STILL SPENT, and it is still drawn: a figure that has gone
 * keeps its slot, hollow, in the same place. The five are the FIRST five in the
 * table's order and never a filtered set, so the hole a spent figure leaves does
 * not move — and the figure in play is appended when it falls outside them,
 * because the one figure that must never be off screen is the one the round is
 * being played on.
 *
 * It is not a modal and must not become one. The whole point of spending a
 * figure is seeing the hole it leaves, and a separate "choose a figure" sheet
 * would put the decision on a different surface from the record of what it
 * costs. MORE is a disclosure in the row itself, which is the difference.
 *
 * The glyphs are `duelSubjects`, shared with the duel's question: one figure has
 * one pictogram in this product, and a reader who learns it in one game reads it
 * in the other.
 */

/** How many chips the row shows before MORE. Five fit a 375px phone at this size. */
const COLLAPSED = 5;

export default function OvertakeSpecBoard({
    spent = [],
    inPlay = null,
    direction = null,
    vetoed = null,
    card = null,
    onName,
}) {
    const t = useTranslate();
    const [isOpen, setOpen] = useState(false);
    const gone = new Set(spent);
    const all = listDuelSpecKeys();
    const shown = visibleSpecs(all, inPlay, isOpen);

    return (
        <section className="overtake-board-band">
            <h2 className="overtake-board__title">{t('play.board.title')}</h2>

            <ul className="overtake-board" aria-label={t('play.board')}>
                {shown.map((spec) => {
                    const isSpent = gone.has(spec);
                    const isLive = spec === inPlay;
                    // Naming is offered only for a figure this card can actually
                    // be played on, so a reader never taps a figure and is told
                    // no.
                    const canName = Boolean(onName)
                        && !isSpent
                        && spec !== vetoed
                        && Boolean(card)
                        && isPlayableOn(card, spec);
                    // While a round is live the glyph shows the direction it is
                    // being PLAYED in, which a counter-call may have inverted.
                    const arrow = (isLive ? direction : baseDirection(spec)) === 'lower' ? '▼' : '▲';
                    const Glyph = duelSubjectIcon(spec);
                    const className = [
                        'overtake-board__chip',
                        isSpent ? 'is-spent' : '',
                        isLive ? 'is-live' : '',
                        baseDirection(spec) === 'lower' ? 'is-lower' : '',
                        canName ? 'is-namable' : '',
                    ].filter(Boolean).join(' ');
                    const inside = (
                        <>
                            {Glyph ? (
                                <Glyph className="overtake-board__glyph" size={22} />
                            ) : (
                                <span className="overtake-board__mark" aria-hidden="true" />
                            )}
                            <span className="overtake-board__label">{t(specLabelKey(spec))}</span>
                            {/* Decorative: the direction is in the chip's
                                accessible name through the label beside it, and
                                an arrow announced as "black up-pointing
                                triangle" is furniture. */}
                            <span className="overtake-board__arrow" aria-hidden="true">{arrow}</span>
                        </>
                    );

                    return (
                        <li className="overtake-board__slot" key={spec}>
                            {canName ? (
                                <button
                                    type="button"
                                    className={className}
                                    onClick={() => onName(spec)}
                                >
                                    {inside}
                                </button>
                            ) : (
                                /* The chip stays the slot's FIRST ELEMENT CHILD
                                   whether it is a control or a record — a
                                   wrapper here would break the suite that
                                   reaches it that way before it ever reached the
                                   class name. */
                                <span className={className}>{inside}</span>
                            )}
                        </li>
                    );
                })}

                {all.length > COLLAPSED ? (
                    <li className="overtake-board__slot">
                        <button
                            type="button"
                            className="overtake-board__chip overtake-board__chip--more"
                            onClick={() => setOpen((open) => !open)}
                            aria-expanded={isOpen}
                        >
                            <span className="overtake-board__glyph" aria-hidden="true">…</span>
                            <span className="overtake-board__label">
                                {t(isOpen ? 'play.board.fewer' : 'play.board.more')}
                            </span>
                        </button>
                    </li>
                ) : null}
            </ul>
        </section>
    );
}

/**
 * Which figures the row draws.
 *
 * The first five in the table's order, so a spent figure's hole stays where it
 * was; plus the one in play when it falls outside them, because a round can be
 * played on any of the eleven and the figure being played on may never be the
 * one the reader cannot see.
 */
function visibleSpecs(all, inPlay, isOpen) {
    if (isOpen) {
        return all;
    }

    const head = all.slice(0, COLLAPSED);

    return inPlay && !head.includes(inPlay) ? [...head, inPlay] : head;
}
